import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  FaBars,
  FaBell,
  FaBookOpen,
  FaCalendarAlt,
  FaChartLine,
  FaChevronDown,
  FaClipboardList,
  FaGlobeAfrica,
  FaLayerGroup,
  FaMedal,
  FaMoneyCheckAlt,
  FaNewspaper,
  FaRegUserCircle,
  FaRunning,
  FaSchool,
  FaShieldAlt,
  FaStore,
  FaTachometerAlt,
  FaTimes,
  FaUserFriends,
  FaUsers,
  FaWallet,
} from "react-icons/fa";
import { PortalAccessProfile } from "types/api";
import { getGroupedPortalNavItems, getPortalTypeLabel } from "utils/portal-access";
import {
  BrandArea,
  BrandDivider,
  BrandMark,
  BrandRow,
  GroupActionCluster,
  GroupButton,
  GroupCard,
  GroupChildren,
  GroupChevron,
  GroupCopy,
  GroupList,
  GroupTitle,
  ItemIcon,
  ItemLabel,
  ItemLink,
  MenuScroll,
  MobileToggle,
  SectionEyebrow,
  WorkspaceBadge,
  WorkspaceRow,
} from "./styles/nav.styles";
import {
  NavEyebrowText,
  NavIllustrationImage,
  NavMobileOverlay,
  NavPageHeading,
  NavSectionBody,
  NavSidebar,
  NavTextGroup,
} from "components/molecules/nav/styles/nav.inline.styles";

const navIcons: Record<string, React.ReactNode> = {
  "age-groups": <FaLayerGroup />,
  classes: <FaBookOpen />,
  countries: <FaGlobeAfrica />,
  dashboard: <FaTachometerAlt />,
  events: <FaCalendarAlt />,
  "followed-athletes": <FaMedal />,
  merchandise: <FaStore />,
  news: <FaNewspaper />,
  notifications: <FaBell />,
  orders: <FaClipboardList />,
  payments: <FaMoneyCheckAlt />,
  performance: <FaChartLine />,
  "player-profile": <FaRegUserCircle />,
  "player-stats": <FaChartLine />,
  players: <FaMedal />,
  regions: <FaGlobeAfrica />,
  requests: <FaClipboardList />,
  "school-matches": <FaCalendarAlt />,
  "school-scoresheets": <FaClipboardList />,
  "school-calendar": <FaCalendarAlt />,
  "school-players": <FaUserFriends />,
  "school-rosters": <FaClipboardList />,
  "school-roles": <FaShieldAlt />,
  "school-sports": <FaRunning />,
  "school-teams": <FaUsers />,
  schools: <FaSchool />,
  sports: <FaRunning />,
  teams: <FaUsers />,
  users: <FaUserFriends />,
  wallet: <FaWallet />,
};

let persistedSidebarScrollTop = 0;
let persistedOpenGroupKeys: string[] = [];

const resolveOpenGroups = (
  navGroups: Array<{ key: string }>,
  activeGroupKey?: string,
  preferredGroupKeys: string[] = [],
) => {
  const availableGroupKeys = new Set<string>(navGroups.map((group) => String(group.key)));
  return Array.from(
    new Set(
      [...preferredGroupKeys, navGroups[0]?.key, activeGroupKey].filter(
        (groupKey): groupKey is string => Boolean(groupKey) && availableGroupKeys.has(groupKey),
      ),
    ),
  );
};

type NavProps = {
  portalAccess?: PortalAccessProfile | null;
  userRole?: string | null;
};
const Nav: React.FC<NavProps> = ({ portalAccess, userRole }) => {
  const history = useHistory();
  const location = useLocation();
  const activePath = location.pathname;
  const navGroups = useMemo(
    () =>
      getGroupedPortalNavItems(portalAccess).map((group) => ({
        ...group,
        items: group.items.map((item) => ({
          ...item,
          icon: navIcons[item.iconKey] || <FaTachometerAlt />,
        })),
      })),
    [portalAccess],
  );
  const activeGroupKey = useMemo(
    () =>
      navGroups.find((group) =>
        group.items.some((item) => activePath === item.path || activePath.startsWith(`${item.path}/`)),
      )?.key || navGroups[0]?.key,
    [navGroups, activePath],
  );
  const [isOpen, setIsOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState<string[]>(() =>
    resolveOpenGroups(navGroups as Array<{ key: string }>, activeGroupKey, persistedOpenGroupKeys),
  );
  const menuScrollRef = useRef<HTMLDivElement | null>(null);
  const workspaceLabel = getPortalTypeLabel(portalAccess, userRole);
  const saveMenuScrollPosition = () => {
    if (!menuScrollRef.current) {
      return;
    }
    persistedSidebarScrollTop = menuScrollRef.current.scrollTop;
  };

  useLayoutEffect(() => {
    const node = menuScrollRef.current;
    if (!node) {
      return;
    }
    node.scrollTop = persistedSidebarScrollTop;
  }, []);

  useEffect(() => {
    setOpenGroups((current) => {
      return resolveOpenGroups(navGroups as Array<{ key: string }>, activeGroupKey, current);
    });
  }, [activeGroupKey, navGroups]);

  useEffect(() => {
    persistedOpenGroupKeys = openGroups;
  }, [openGroups]);

  useEffect(() => {
    if (!isOpen || typeof document === "undefined") {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  useEffect(
    () => () => {
      saveMenuScrollPosition();
      persistedOpenGroupKeys = openGroups;
    },
    [openGroups],
  );

  const handleNavigate = () => {
    saveMenuScrollPosition();
    setIsOpen(false);
  };

  const toggleGroup = (groupKey: string) => {
    saveMenuScrollPosition();
    setOpenGroups((current) =>
      current.includes(groupKey)
        ? current.filter((entry) => entry !== groupKey)
        : [...current, groupKey],
    );
  };
  return (
    <>
      <NavSectionBody>
        <MobileToggle
          aria-expanded={isOpen}
          aria-label={isOpen ? "Close navigation menu" : "Open navigation menu"}
          onClick={() => setIsOpen((current) => !current)}
          type="button"
        >
          {isOpen ? <FaTimes /> : <FaBars />}
        </MobileToggle>
      </NavSectionBody>

      {isOpen ? (
        <NavMobileOverlay aria-label="Close navigation overlay" onClick={() => setIsOpen(false)} />
      ) : null}

      <NavSidebar $isOpen={isOpen} aria-label="Primary navigation">
        <BrandArea>
          <BrandDivider>
            <BrandRow>
              <BrandMark>
                <NavIllustrationImage
                  alt="Athspire logo"
                  src="/assets/images/brand/athspire-logo.svg"
                />
              </BrandMark>
              <NavTextGroup>
                <NavEyebrowText>Athspire</NavEyebrowText>
                <NavPageHeading>Portal</NavPageHeading>
              </NavTextGroup>
            </BrandRow>
            <WorkspaceRow>
              <WorkspaceBadge>{workspaceLabel}</WorkspaceBadge>
            </WorkspaceRow>
          </BrandDivider>
        </BrandArea>

        <MenuScroll ref={menuScrollRef}>
          <SectionEyebrow>Workspace Areas</SectionEyebrow>

          <GroupList>
            {navGroups.map((group) => {
              const isOpenGroup = openGroups.includes(group.key);
              const isActiveGroup = group.items.some(
                (item) => activePath === item.path || activePath.startsWith(`${item.path}/`),
              );
              return (
                <GroupCard $active={isActiveGroup} $open={isOpenGroup} key={group.key}>
                  <GroupButton
                    $active={isActiveGroup}
                    onClick={() => toggleGroup(group.key)}
                    type="button"
                  >
                    <GroupCopy>
                      <GroupTitle>{group.label}</GroupTitle>
                    </GroupCopy>
                    <GroupActionCluster>
                      <GroupChevron $isOpen={isOpenGroup} aria-hidden="true">
                        <FaChevronDown />
                      </GroupChevron>
                    </GroupActionCluster>
                  </GroupButton>

                  {isOpenGroup ? (
                    <GroupChildren>
                      {group.items.map((item) => {
                        const isActiveItem =
                          activePath === item.path || activePath.startsWith(`${item.path}/`);
                        return (
                          <ItemLink
                            $active={isActiveItem}
                            key={item.path}
                            onClick={() => {
                              handleNavigate();
                              if (activePath !== item.path) {
                                history.push(item.path);
                              }
                            }}
                          >
                            <ItemIcon $active={isActiveItem}>{item.icon}</ItemIcon>
                            <ItemLabel>{item.label}</ItemLabel>
                          </ItemLink>
                        );
                      })}
                    </GroupChildren>
                  ) : null}
                </GroupCard>
              );
            })}
          </GroupList>
        </MenuScroll>
      </NavSidebar>
    </>
  );
};
export default Nav;
